import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchScores, saveScore, changeScoreStatus } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import Swal from 'sweetalert2';
import useButtonAccess from "components/Common/useButtonAccess";

const ScoreMaster = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const { scoresList, loading, success, error } = useSelector((state) => state.ScoreMasterReducer || {});
  const [authDetails, setAuthDetails] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0]);
    const unitId = authUserDetails[0].unit_id;
    dispatch(fetchScores(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchScores(authDetails?.unit_id));
      setIsEdit(false);
      setEditId(null);
      formik.resetForm();
    }
  }, [authDetails, success, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      score_id: 0,
      from_score: "",
      to_score: "",
      interval: ""
    },
    validationSchema: Yup.object({
      from_score: Yup.number().required("From Score is required"),
      to_score: Yup.number().required("To Score is required"),
      interval: Yup.number().required("Interval is required")
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("score_id", values.score_id);
      formData.append("from_score", values.from_score);
      formData.append("to_score", values.to_score);
      formData.append("interval", values.interval);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);

      dispatch(saveScore(formData));
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      score_id: row.score_id,
      from_score: row.from_score,
      to_score: row.to_score,
      interval: row.interval
    });
    setIsEdit(true);
    setEditId(row.score_id);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEditId(null);
  };

  const handleToggleStatus = (row) => {
    Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete this score range`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("score_id", row.score_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeScoreStatus(formData));
      }
    });
  };

  const columns = useMemo(() => {
    const actionColumn = (canEdit || canDelete)
      ? {
          Header: "Action",
          className: "status-col",
          applyHeaderClass: true,
          Cell: (cellProps) => {
            const { score_id } = cellProps.row.original;
            return (
              <div className="d-flex gap-3">
                {canEdit && (
                  <Link
                    to="#"
                    className="text-success"
                    onClick={() => handleEdit(cellProps.row.original)}
                  >
                    <i
                      className="bx bxs-edit font-size-18"
                      id={`edit-tooltip-${score_id}`}
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target={`edit-tooltip-${score_id}`}
                    >
                      Edit
                    </UncontrolledTooltip>
                  </Link>
                )}

                {canDelete && (
                  <Link
                    to="#"
                    className="text-danger"
                    onClick={() => handleToggleStatus(cellProps.row.original)}
                  >
                    <i
                      className="mdi mdi-delete font-size-18"
                      id={`delete-tooltip-${score_id}`}
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target={`delete-tooltip-${score_id}`}
                    >
                      Delete
                    </UncontrolledTooltip>
                  </Link>
                )}
              </div>
            );
          },
        }
      : null;

    return [
      ...(actionColumn ? [actionColumn] : []),
      {
        Header: "S.No",
        Cell: ({ row }) => scoresList.length - row.index,
        className: "serial-no",
        applyHeaderClass: true,
      },
      { Header: "From Score", accessor: "from_score" },
      { Header: "To Score", accessor: "to_score" },
      { Header: "Interval", accessor: "interval" },
    ];
  }, [scoresList, canEdit, canDelete]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Setup" breadcrumbItem="Score Master" />

          {(canCreate || canEdit) && (
            <Card>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <Row className="mb-3 g-3">
                    <Col md="4">
                      <FormGroup>
                        <Label for="from_score">
                          From Score <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="from_score"
                          name="from_score"
                          type="number"
                          value={formik.values.from_score}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.from_score &&
                            Boolean(formik.errors.from_score)
                          }
                        />
                        {formik.touched.from_score && formik.errors.from_score && (
                          <div className="text-danger">{formik.errors.from_score}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="to_score">
                          To Score <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="to_score"
                          name="to_score"
                          type="number"
                          value={formik.values.to_score}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.to_score &&
                            Boolean(formik.errors.to_score)
                          }
                        />
                        {formik.touched.to_score && formik.errors.to_score && (
                          <div className="text-danger">{formik.errors.to_score}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="interval">
                          Interval <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="interval"
                          name="interval"
                          type="number"
                          step="0.1"
                          value={formik.values.interval}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.interval &&
                            Boolean(formik.errors.interval)
                          }
                        />
                        {formik.touched.interval && formik.errors.interval && (
                          <div className="text-danger">{formik.errors.interval}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="d-flex justify-content-end gap-2">
                      <Button type="submit" color="primary" className="btn button-save">
                        <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                      </Button>
                      <Button type="button" color="secondary" onClick={handleCancel} className="btn button-cancel">
                        <i className="fas fa-sync me-1" /> Cancel
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          )}

          {canRead && (
            <Card>
              <CardBody>
                <TableContainer
                  columns={columns}
                  data={scoresList || []}
                  isGlobalFilter={true}
                  customPageSize={10}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ScoreMaster;
